import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { InboxService } from '../../core/services/inbox/inbox.service';
import { AlertService } from '../../core/services/alert/alert.service';
import { InboxMessageItem, InboxMessageDetail, InboxPaginationMeta } from '../../core/interfaces/communication.interface';
import { InboxDetailComponent } from './inbox-detail/inbox-detail.component';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-email-inbox',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, InboxDetailComponent],
  templateUrl: './email-inbox.component.html',
  styleUrl: './email-inbox.component.css',
})
export class EmailInboxComponent implements OnInit, OnDestroy {
  messages: InboxMessageItem[] = [];
  selectedMessage: InboxMessageDetail | null = null;
  selectedUid: number | null = null;
  selectedFolder: string = 'inbox';
  isLoading = true;
  isLoadingDetail = false;
  filterUnseen = false;
  notConfigured = false;

  // Folder
  currentFolder: 'inbox' | 'sent' = 'inbox';

  // Pagination
  currentPage = 1;
  perPage = 20;
  perPageOptions = [10, 20, 50];
  meta: InboxPaginationMeta = { current_page: 1, from: null, to: null, total: 0, per_page: 20, last_page: 1 };

  // Search
  searchTerm = '';
  searchBody = false;
  isSearching = false;
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  // Thread view
  threadMessages: InboxMessageDetail[] = [];
  isLoadingThread = false;
  showThread = false;
  threadMeta: { thread_id: string; count: number; truncated: boolean } | null = null;
  collapsedThreadUids: Set<string> = new Set();

  // Local "opened" tracking (since IMAP read doesn't mark as seen)
  openedUids: Set<string> = new Set();

  constructor(
    private inboxService: InboxService,
    private alertService: AlertService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.loadMessages();
    this.searchSubject.pipe(
      debounceTime(600),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    ).subscribe(term => {
      this.searchTerm = term;
      this.currentPage = 1;
      this.loadMessages();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadMessages(refresh: boolean = false): void {
    this.isLoading = true;
    this.isSearching = !!this.searchTerm.trim();
    this.inboxService.getMessages({
      page: this.currentPage,
      perPage: this.perPage,
      unseen: this.filterUnseen,
      folder: this.currentFolder,
      search: this.searchTerm || undefined,
      searchBody: this.searchBody,
      refresh,
    }).subscribe({
      next: (res) => {
        this.messages = res.data || [];
        this.meta = res.meta || this.meta;
        this.isLoading = false;
        this.isSearching = false;
        this.notConfigured = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.isSearching = false;
        if (err.status === 404) {
          const errorData = err.error;
          if (errorData?.errors?.account_imaps) {
            this.notConfigured = true;
            return;
          }
        }
        if (err.status === 502) {
          this.alertService.error('swal_messages.account_imaps.imap_connection_failed');
          return;
        }
        this.alertService.error('communication.inbox.error_loading');
      },
    });
  }

  switchFolder(folder: 'inbox' | 'sent'): void {
    if (this.currentFolder === folder) return;
    this.currentFolder = folder;
    this.currentPage = 1;
    this.searchTerm = '';
    this.filterUnseen = false;
    this.closeDetail();
    this.loadMessages();
  }

  selectMessage(msg: InboxMessageItem): void {
    const folder = msg.folder || this.currentFolder;
    this.selectedUid = msg.uid;
    this.selectedFolder = folder;
    this.selectedMessage = null;
    this.showThread = false;
    this.threadMessages = [];
    this.isLoadingDetail = true;
    this.openedUids.add(`${folder}:${msg.uid}`);

    // If the message has a thread_id, load the full thread
    if (msg.thread_id) {
      this.isLoadingThread = true;
      this.inboxService.getThread(msg.uid, folder, true).subscribe({
        next: (res) => {
          this.threadMessages = res.data || [];
          this.threadMeta = res.meta || null;
          this.isLoadingThread = false;
          this.isLoadingDetail = false;
          this.showThread = this.threadMessages.length > 1;
          // If single message or thread failed, fall back to detail
          if (this.threadMessages.length === 1) {
            this.selectedMessage = this.threadMessages[0] as InboxMessageDetail;
          } else if (this.threadMessages.length === 0) {
            this.loadSingleMessage(msg.uid, folder);
          }
        },
        error: () => {
          // Thread not supported or failed — fall back to single message
          this.isLoadingThread = false;
          this.loadSingleMessage(msg.uid, folder);
        },
      });
    } else {
      this.loadSingleMessage(msg.uid, folder);
    }
  }

  private loadSingleMessage(uid: number, folder: string): void {
    this.inboxService.getMessage(uid, folder).subscribe({
      next: (res) => {
        this.selectedMessage = res.data;
        this.isLoadingDetail = false;
      },
      error: (err) => {
        this.isLoadingDetail = false;
        if (err.status === 502) {
          this.alertService.error('swal_messages.account_imaps.imap_connection_failed');
        } else {
          this.alertService.error('swal_messages.inbox_messages.inbox_message_not_found');
        }
      },
    });
  }

  closeDetail(): void {
    this.selectedMessage = null;
    this.selectedUid = null;
    this.selectedFolder = 'inbox';
    this.isLoadingDetail = false;
    this.showThread = false;
    this.threadMessages = [];
    this.threadMeta = null;
    this.collapsedThreadUids.clear();
  }

  toggleUnseen(): void {
    this.filterUnseen = !this.filterUnseen;
    this.currentPage = 1;
    this.loadMessages();
  }

  onSearchInput(term: string): void {
    this.searchSubject.next(term);
  }

  onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.currentPage = 1;
      this.loadMessages();
    }
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.currentPage = 1;
    this.loadMessages();
  }

  onRefresh(): void {
    this.loadMessages(true);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.meta.last_page) return;
    this.currentPage = page;
    this.loadMessages();
  }

  onPerPageChange(value: number): void {
    this.perPage = value;
    this.currentPage = 1;
    this.loadMessages();
  }

  onReplySent(): void {
    // Success alert is handled by InboxDetailComponent
  }

  isOpened(msg: InboxMessageItem): boolean {
    const folder = msg.folder || this.currentFolder;
    return this.openedUids.has(`${folder}:${msg.uid}`);
  }

  formatDate(dateStr: string | null): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) {
      return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
  }

  getFromDisplay(from: { name: string; email: string }[]): string {
    if (!from || from.length === 0) return '';
    return from[0].name || from[0].email;
  }

  isThreadMsgExpanded(msg: InboxMessageDetail): boolean {
    const key = `${msg.folder || 'inbox'}:${msg.uid}`;
    return !this.collapsedThreadUids.has(key);
  }

  toggleThreadMsg(msg: InboxMessageDetail): void {
    const key = `${msg.folder || 'inbox'}:${msg.uid}`;
    if (this.collapsedThreadUids.has(key)) {
      this.collapsedThreadUids.delete(key);
    } else {
      this.collapsedThreadUids.add(key);
    }
  }

  /** Get the last inbox message from thread (for reply) */
  getLastInboxMessage(): InboxMessageDetail | null {
    if (!this.threadMessages || this.threadMessages.length === 0) return null;
    for (let i = this.threadMessages.length - 1; i >= 0; i--) {
      if ((this.threadMessages[i] as any).folder === 'inbox') {
        return this.threadMessages[i] as InboxMessageDetail;
      }
    }
    return null;
  }

  /** For sent folder, show recipients instead of sender */
  getDisplayName(msg: InboxMessageItem): string {
    if (msg.folder === 'sent' || this.currentFolder === 'sent') {
      if (!msg.to || msg.to.length === 0) return '';
      const first = msg.to[0].name || msg.to[0].email;
      return msg.to.length > 1 ? `${first} (+${msg.to.length - 1})` : first;
    }
    return this.getFromDisplay(msg.from);
  }

  getDisplayEmail(msg: InboxMessageItem): string {
    if (msg.folder === 'sent' || this.currentFolder === 'sent') {
      if (!msg.to || msg.to.length === 0) return '';
      return msg.to[0].email || '';
    }
    if (!msg.from || msg.from.length === 0) return '';
    return msg.from[0].email || '';
  }
}
